import { useEffect, useRef, useState } from "react";
import { Sparkles, Plus, Send, Trash2, Loader2, MessageSquare, Bot, User, ListChecks, FolderKanban, Phone, FileText, ThumbsUp, ThumbsDown } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { AiDidCommand } from "@/components/AiDidCommand";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

const SUGGESTIONS_BY_ROLE = {
  staff: [
    "Which projects are at risk this week (behind their target timeline)?",
    "How do I set up priority tagging for tickets?",
    "How many phone numbers do we have, counted by application?",
    "Summarize project health across all projects.",
    "Which quotes are at risk or expiring soon?",
    "What activity happened recently on the platform?",
    "How many DIDs are SMS-enabled?",
  ],
  partner: [
    "Which of my projects are at risk this week (behind target)?",
    "How do I install the desktop app?",
    "Summarize the health of my projects.",
    "Which of my quotes are at risk or expiring soon?",
    "How many DID numbers are assigned to my organization?",
    "What's the status of my most recent projects?",
    "How many of my projects are active vs completed?",
  ],
  customer: [
    "Which of my projects are at risk this week (behind target)?",
    "How do I join a meeting?",
    "Summarize the health of my projects.",
    "How many of my projects are still active?",
    "Which of my projects are completed?",
    "What's the status of my most recent projects?",
    "What's the go-live timeline for my active projects?",
  ],
};

/** Renders assistant replies as light markdown: headings, lists, quotes, **bold**, and clickable KB links. */
function AssistantText({ content }) {
  const navigate = useNavigate();
  const inline = (text, kp) => {
    const out = [];
    const regex = /\[([^\]]+)\]\(([^)]+)\)|\*\*([^*]+)\*\*/g;
    let last = 0, m, k = 0;
    const push = (s) => { if (s) out.push(<span key={`${kp}t${k++}`}>{s}</span>); };
    while ((m = regex.exec(text)) !== null) {
      push(text.slice(last, m.index));
      if (m[1] !== undefined) {
        const label = m[1], url = m[2];
        out.push(url.startsWith("/")
          ? <button key={`${kp}l${k++}`} type="button" onClick={() => navigate(url)} data-testid="claude-kb-link"
              className="font-medium text-primary underline underline-offset-2 hover:opacity-80">{label}</button>
          : <a key={`${kp}l${k++}`} href={url} target="_blank" rel="noreferrer"
              className="font-medium text-primary underline underline-offset-2 hover:opacity-80">{label}</a>);
      } else {
        out.push(<strong key={`${kp}b${k++}`}>{m[3]}</strong>);
      }
      last = regex.lastIndex;
    }
    push(text.slice(last));
    return out;
  };
  const blocks = [];
  const lines = (content || "").split("\n");
  const isRow = (l) => /^\s*\|.*\|\s*$/.test(l || "");
  const isSep = (l) => /^\s*\|[\s:|-]+\|\s*$/.test(l || "");
  const cells = (l) => l.trim().replace(/^\||\|$/g, "").split("|").map((c) => c.trim());
  for (let i = 0; i < lines.length; i++) {
    const raw = lines[i];
    const line = raw.trimEnd();
    // Iter 120 — render markdown pipe tables as real tables instead of
    // dumping raw "| a | b |" rows into the reply.
    if (isRow(line) && isSep(lines[i + 1])) {
      const head = cells(line);
      const rows = [];
      let j = i + 2;
      while (j < lines.length && isRow(lines[j]) && !isSep(lines[j])) { rows.push(cells(lines[j])); j++; }
      blocks.push(
        <div key={`tb${i}`} className="my-2 overflow-x-auto" data-testid="claude-md-table">
          <table className="w-full border-collapse text-[13px]">
            <thead>
              <tr>{head.map((h, hi) => (
                <th key={hi} className="border border-slate-200 bg-slate-50 px-2 py-1 text-left font-semibold text-slate-700">
                  {inline(h, `th${i}-${hi}`)}
                </th>
              ))}</tr>
            </thead>
            <tbody>{rows.map((r, ri) => (
              <tr key={ri}>{head.map((_, ci) => (
                <td key={ci} className="border border-slate-200 px-2 py-1 align-top text-slate-600">
                  {inline(r[ci] || "", `td${i}-${ri}-${ci}`)}
                </td>
              ))}</tr>
            ))}</tbody>
          </table>
        </div>,
      );
      i = j - 1;
      continue;
    }
    if (!line.trim()) { blocks.push(<div key={`sp${i}`} className="h-1.5" />); continue; }
    if (/^(-{3,}|\*{3,}|_{3,})$/.test(line.trim())) { blocks.push(<hr key={`hr${i}`} className="my-1.5 border-slate-200" />); continue; }
    let mm;
    if ((mm = line.match(/^(#{1,6})\s+(.*)$/))) {
      const cls = mm[1].length <= 2 ? "mt-1 text-sm font-bold" : "mt-1 text-[13px] font-semibold";
      blocks.push(<div key={`h${i}`} className={cls}>{inline(mm[2], `h${i}`)}</div>);
    } else if ((mm = line.match(/^>\s?(.*)$/))) {
      blocks.push(<div key={`q${i}`} className="border-l-2 border-slate-300 pl-2 text-slate-600">{inline(mm[1], `q${i}`)}</div>);
    } else if ((mm = line.match(/^[-*]\s+(.*)$/))) {
      blocks.push(<div key={`li${i}`} className="flex gap-1.5"><span className="text-slate-400">•</span><span>{inline(mm[1], `li${i}`)}</span></div>);
    } else if ((mm = line.match(/^(\d+)\.\s+(.*)$/))) {
      blocks.push(<div key={`ol${i}`} className="flex gap-1.5"><span className="shrink-0 text-slate-400">{mm[1]}.</span><span>{inline(mm[2], `ol${i}`)}</span></div>);
    } else {
      blocks.push(<div key={`p${i}`}>{inline(line, `p${i}`)}</div>);
    }
  }
  return <div className="space-y-0.5">{blocks}</div>;
}

export default function AskClaude() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isStaff = user?.base_role === "staff";
  const [didCmdOpen, setDidCmdOpen] = useState(false);
  const FALLBACK = SUGGESTIONS_BY_ROLE[user?.base_role] || SUGGESTIONS_BY_ROLE.staff;
  const [suggestions, setSuggestions] = useState(FALLBACK);
  const [sessions, setSessions] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [loadingMsgs, setLoadingMsgs] = useState(false);
  const scrollRef = useRef(null);
  const [params, setParams] = useSearchParams();

  const rate = async (mid, rating, idx) => {
    if (!mid) return;
    setMessages((m) => m.map((x, i) => i === idx ? { ...x, feedback: rating } : x));
    try { await api.post(`/assistant/messages/${mid}/feedback`, { rating }); }
    catch { toast.error("Could not save feedback."); }
  };

  const loadSessions = async () => {
    try { const { data } = await api.get("/assistant/sessions"); setSessions(data); return data; }
    catch { return []; }
  };

  // Iter 120 — captured on first render (a ref, so a StrictMode remount or the
  // `setParams({})` cleanup below can't make us forget we arrived via
  // "Ask AI about this"). Used to skip auto-opening the newest conversation,
  // which would otherwise overwrite the optimistic user bubble.
  const askParamRef = useRef(new URLSearchParams(window.location.search).get("ask") || "");

  useEffect(() => {
    loadSessions().then((s) => { if (!askParamRef.current && s.length) selectSession(s[0].session_id); });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { api.get("/assistant/suggestions").then((r) => { if (r.data?.suggestions?.length) setSuggestions(r.data.suggestions); }).catch(() => {}); }, []);
  useEffect(() => { if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight; }, [messages, sending]);
  // "Ask AI about this" deep link — auto-send a pre-seeded question once.
  const askedRef = useRef(false);
  useEffect(() => {
    const ask = params.get("ask") || askParamRef.current;
    if (ask && !askedRef.current) {
      askedRef.current = true;
      setParams({}, { replace: true });
      send(ask);
    }
    // eslint-disable-next-line
  }, [params]);

  const selectSession = async (sid) => {
    setActiveId(sid); setLoadingMsgs(true); setMessages([]);
    try { const { data } = await api.get(`/assistant/sessions/${sid}/messages`); setMessages(data); }
    catch { toast.error("Could not load conversation."); }
    finally { setLoadingMsgs(false); }
  };

  const newSession = async () => {
    try {
      const { data } = await api.post("/assistant/sessions", {});
      setSessions((s) => [data, ...s]);
      setActiveId(data.session_id); setMessages([]);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const deleteSession = async (sid, e) => {
    e.stopPropagation();
    try {
      await api.delete(`/assistant/sessions/${sid}`);
      const remaining = sessions.filter((s) => s.session_id !== sid);
      setSessions(remaining);
      if (activeId === sid) {
        if (remaining.length) selectSession(remaining[0].session_id);
        else { setActiveId(null); setMessages([]); }
      }
    } catch { toast.error("Could not delete conversation."); }
  };

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || sending) return;
    let sid = activeId;
    if (!sid) {
      try { const { data } = await api.post("/assistant/sessions", {}); setSessions((s) => [data, ...s]); sid = data.session_id; setActiveId(sid); }
      catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); return; }
    }
    setInput("");
    setMessages((m) => [...m, { role: "user", content: msg, created_at: new Date().toISOString() }]);
    setSending(true);
    try {
      const { data } = await api.post(`/assistant/sessions/${sid}/chat`, { message: msg });
      setMessages((m) => [...m, { role: "assistant", content: data.reply, sources: data.sources, message_id: data.message_id, feedback: null, created_at: data.created_at }]);
      loadSessions();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "The assistant could not respond.");
      setMessages((m) => m.slice(0, -1));
      setInput(msg);
    } finally { setSending(false); }
  };

  const onKeyDown = (e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } };

  return (
    <div data-testid="ask-claude-page">
      <PageHeader testid="ask-claude-header" title="Ask AI" subtitle="Read-only AI assistant grounded in your live platform data.">
        <Button data-testid="new-conversation-button" onClick={newSession} className="bg-primary hover:bg-primary/90 text-primary-foreground">
          <Plus className="h-4 w-4" /> New chat
        </Button>
      </PageHeader>

      {isStaff && (
        <div className="mb-4 flex flex-wrap items-center gap-2" data-testid="claude-quick-actions">
          <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Quick actions:</span>
          <Button variant="outline" size="sm" data-testid="qa-incident-task" onClick={() => navigate("/tasks?ai=1")} className="border-violet-300 text-violet-700 hover:bg-violet-50 dark:border-violet-800 dark:text-violet-300 dark:hover:bg-violet-950/40"><ListChecks className="h-4 w-4" /> Draft incident task</Button>
          <Button variant="outline" size="sm" data-testid="qa-draft-project" onClick={() => navigate("/projects?ai=1")} className="border-violet-300 text-violet-700 hover:bg-violet-50 dark:border-violet-800 dark:text-violet-300 dark:hover:bg-violet-950/40"><FolderKanban className="h-4 w-4" /> Draft a project</Button>
          <Button variant="outline" size="sm" data-testid="qa-did-command" onClick={() => setDidCmdOpen(true)} className="border-violet-300 text-violet-700 hover:bg-violet-50 dark:border-violet-800 dark:text-violet-300 dark:hover:bg-violet-950/40"><Phone className="h-4 w-4" /> Number (DID) command</Button>
        </div>
      )}

      <AiDidCommand open={didCmdOpen} onOpenChange={setDidCmdOpen} />

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[260px_1fr]">
        {/* Sessions sidebar */}
        <div className="rounded-xl border border-slate-200 bg-white shadow-sm" data-testid="claude-sessions">
          <div className="border-b border-slate-100 px-4 py-3 text-xs font-semibold uppercase tracking-wide text-slate-400">Conversations</div>
          <div className="max-h-[60vh] overflow-y-auto p-2">
            {sessions.length === 0 ? (
              <p className="px-2 py-6 text-center text-sm text-slate-400">No conversations yet.</p>
            ) : sessions.map((s) => (
              <button key={s.session_id} data-testid={`claude-session-${s.session_id}`} onClick={() => selectSession(s.session_id)}
                className={`group mb-1 flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-left text-sm transition-colors ${activeId === s.session_id ? "bg-primary/10 text-primary" : "text-slate-600 hover:bg-slate-50"}`}>
                <MessageSquare className="h-4 w-4 shrink-0 opacity-60" />
                <span className="flex-1 truncate">{s.title}</span>
                <Trash2 data-testid={`delete-session-${s.session_id}`} onClick={(e) => deleteSession(s.session_id, e)}
                  className="h-3.5 w-3.5 shrink-0 text-slate-300 opacity-0 transition-opacity hover:text-red-500 group-hover:opacity-100" />
              </button>
            ))}
          </div>
        </div>

        {/* Chat area */}
        <div className="flex h-[68vh] flex-col rounded-xl border border-slate-200 bg-white shadow-sm">
          <div ref={scrollRef} className="flex-1 space-y-4 overflow-y-auto p-5" data-testid="claude-messages">
            {loadingMsgs ? (
              <div className="flex h-full items-center justify-center"><Loader2 className="h-5 w-5 animate-spin text-slate-300" /></div>
            ) : messages.length === 0 ? (
              <div className="flex h-full flex-col items-center justify-center text-center">
                <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-primary text-primary-foreground shadow-lg"><Sparkles className="h-7 w-7" /></span>
                <h3 className="mt-4 text-lg font-semibold text-slate-800">Ask anything!</h3>
                <p className="mt-1 max-w-md text-sm text-slate-500">Claude can read your projects, quotes, DIDs, inventory and activity — but cannot make changes (while currently in limited BETA).</p>
                <div className="mt-5 grid w-full max-w-lg grid-cols-1 gap-2 sm:grid-cols-2">
                  {suggestions.map((q, i) => (
                    <button key={i} data-testid={`claude-suggestion-${i}`} onClick={() => send(q)}
                      className="rounded-lg border border-slate-200 px-3 py-2.5 text-left text-sm text-slate-600 transition-colors hover:border-primary hover:bg-primary/5 hover:text-primary">
                      {q}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              messages.map((m, i) => (
                <div key={i} className={`flex gap-3 ${m.role === "user" ? "flex-row-reverse" : ""}`} data-testid={`claude-msg-${m.role}-${i}`}>
                  <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${m.role === "user" ? "bg-primary/15 text-primary" : "bg-primary text-primary-foreground"}`}>
                    {m.role === "user" ? <User className="h-4 w-4" /> : <Bot className="h-4 w-4" />}
                  </span>
                  {m.role === "assistant" ? (
                    <div className="flex max-w-[78%] flex-col items-start gap-1.5">
                      <div className="rounded-2xl bg-slate-100 px-4 py-2.5 text-sm leading-relaxed text-slate-800">
                        <AssistantText content={m.content} />
                      </div>
                      {m.sources?.length > 0 && (
                        <div className="flex flex-wrap items-center gap-1.5" data-testid="claude-sources">
                          <span className="text-[11px] font-medium text-slate-400">Sources:</span>
                          {m.sources.map((s) => (
                            <button key={s.slug} type="button" onClick={() => navigate(`/kb/a/${s.slug}`)}
                              data-testid={`claude-source-${s.slug}`}
                              className="inline-flex max-w-[15rem] items-center gap-1 truncate rounded-full border border-primary/30 bg-primary/5 px-2 py-0.5 text-[11px] font-medium text-primary transition-colors hover:bg-primary/10">
                              <FileText className="h-3 w-3 shrink-0" /> <span className="truncate">{s.name}</span>
                            </button>
                          ))}
                        </div>
                      )}
                      {m.message_id && (
                        <div className="flex items-center gap-1" data-testid={`claude-feedback-${i}`}>
                          <button type="button" title="Helpful" data-testid={`claude-thumbs-up-${i}`} onClick={() => rate(m.message_id, "up", i)}
                            className={`rounded-md p-1 transition-colors hover:bg-slate-100 ${m.feedback === "up" ? "text-emerald-600" : "text-slate-300 hover:text-slate-500"}`}>
                            <ThumbsUp className="h-3.5 w-3.5" />
                          </button>
                          <button type="button" title="Not helpful — flags this for editors" data-testid={`claude-thumbs-down-${i}`} onClick={() => rate(m.message_id, "down", i)}
                            className={`rounded-md p-1 transition-colors hover:bg-slate-100 ${m.feedback === "down" ? "text-rose-600" : "text-slate-300 hover:text-slate-500"}`}>
                            <ThumbsDown className="h-3.5 w-3.5" />
                          </button>
                          {m.feedback === "down" && <span className="text-[11px] text-slate-400">Flagged for review</span>}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="max-w-[78%] whitespace-pre-wrap rounded-2xl bg-primary px-4 py-2.5 text-sm leading-relaxed text-primary-foreground">
                      {m.content}
                    </div>
                  )}
                </div>
              ))
            )}
            {sending && (
              <div className="flex gap-3" data-testid="claude-thinking">
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary text-primary-foreground"><Bot className="h-4 w-4" /></span>
                <div className="rounded-2xl bg-slate-100 px-4 py-3"><Loader2 className="h-4 w-4 animate-spin text-slate-400" /></div>
              </div>
            )}
          </div>

          <div className="border-t border-slate-100 p-3">
            <div className="flex items-end gap-2">
              <Textarea data-testid="claude-input" value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={onKeyDown}
                placeholder="Ask about projects, quotes, DIDs, activity…" rows={1} className="max-h-32 min-h-[44px] resize-none" />
              <Button data-testid="claude-send-button" onClick={() => send()} disabled={sending || !input.trim()} className="h-11 shrink-0 bg-primary hover:bg-primary/90 text-primary-foreground">
                {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              </Button>
            </div>
            <p className="mt-1.5 px-1 text-[11px] text-slate-400">Claude is read-only and grounded in current platform data. Press Enter to send, Shift+Enter for a new line.</p>
          </div>
        </div>
      </div>
    </div>
  );
}
